import React, { useEffect, useState } from "react";
import "./FundraiserRepQuickStats.css";
import config from "../../../config";
import { RepresentativeDashboard } from "../../../../src/Interfaces/Dashboards";

interface FundraiserStatsProps {
  repDashboard: RepresentativeDashboard | null;
}

const RepDashboard: React.FC<FundraiserStatsProps> = ({ repDashboard }) => {
  const [selectedTab, setSelectedTab] = useState<string>("allTime"); // State to track selected tab

  const handleStartNewFundraiser = () => {
    window.open("/create-fundraiser/org-info");
  };

  // Function to handle tab change
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  // Function to format the numbers as currency with a dollar sign and two decimal places
  const formatCurrency = (value: number | string): string => {
    const numberValue = parseFloat(value.toString());
    return isNaN(numberValue) ? "$0.00" : `$${numberValue.toFixed(2)}`;
  };

  // Function to render sales and commissions based on the selected tab
  const getMetrics = () => {
    if (!repDashboard)
      return { sales: "Loading...", commissions: "Loading..." };

    switch (selectedTab) {
      case "thisWeek":
        return {
          sales: formatCurrency(
            repDashboard.financial_totals.sales_amount_week_to_date
          ),
          commissions: formatCurrency(
            repDashboard.financial_totals.commissions_week_to_date
          ),
        };
      case "thisMonth":
        return {
          sales: formatCurrency(
            repDashboard.financial_totals.sales_amount_month_to_date
          ),
          commissions: formatCurrency(
            repDashboard.financial_totals.commissions_month_to_date
          ),
        };
      case "thisYear":
        return {
          sales: formatCurrency(
            repDashboard.financial_totals.sales_amount_year_to_date
          ),
          commissions: formatCurrency(
            repDashboard.financial_totals.commissions_year_to_date
          ),
        };
      case "allTime":
      default:
        return {
          sales: formatCurrency(
            repDashboard.financial_totals.amount_raised_all_time
          ),
          commissions: formatCurrency(
            repDashboard.financial_totals.commissions_all_time
          ),
        };
    }
  };

  const { sales, commissions } = getMetrics();

  return (
    <div className="rep-dashboard-dashboard">
      <button
        className="rep-dashboard-start-fundraiser"
        onClick={handleStartNewFundraiser}
      >
        Start a New Fundraiser
      </button>
      <div className="rep-dashboard-my-projects">
        <div className="rep-dashboard-active-projects">
          <h3>Active</h3>
          <p>{repDashboard?.fundraiser_counts.active}</p>
        </div>
        <div className="rep-dashboard-completed-projects">
          <h3>Completed</h3>
          <p>{repDashboard?.fundraiser_counts.completed}</p>
        </div>
      </div>
      <div className="rep-dashboard-sales-analytics">
        <div className="rep-dashboard-sales-analytics-header">
          <h3>Sales Analytics</h3>
        </div>
        <div className="rep-dashboard-tabs">
          <button
            className={
              selectedTab === "thisWeek" ? "rep-dashboard-active-tab" : ""
            }
            onClick={() => handleTabChange("thisWeek")}
          >
            This Week
          </button>
          <button
            className={
              selectedTab === "thisMonth" ? "rep-dashboard-active-tab" : ""
            }
            onClick={() => handleTabChange("thisMonth")}
          >
            This Month
          </button>
          <button
            className={
              selectedTab === "thisYear" ? "rep-dashboard-active-tab" : ""
            }
            onClick={() => handleTabChange("thisYear")}
          >
            This Year
          </button>
          <button
            className={
              selectedTab === "allTime" ? "rep-dashboard-active-tab" : ""
            }
            onClick={() => handleTabChange("allTime")}
          >
            All Time
          </button>
        </div>
        <div className="rep-dashboard-analytics-metrics">
          <div className="rep-dashboard-total-sales">
            <div className="rep-dashboard-metric-title">
              <strong>Total Sales:</strong>
            </div>
            <div className="rep-dashboard-metric-value">
              {sales ?? "Loading..."}
            </div>
          </div>
          <div className="rep-dashboard-total-commissions">
            <div className="rep-dashboard-metric-title">
              <strong>Total Commissions:</strong>
            </div>
            <div className="rep-dashboard-metric-value">
              {commissions ?? "Loading..."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepDashboard;
